import React, { useState, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import {
    Box,
    Grid,
    Card,
    Typography,
    Select,
    MenuItem,
    Chip,
    FormControl,
    InputLabel,
    Checkbox,
    ListItemText,
    TextField,
    InputAdornment
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import {
    Assignment as AssignmentIcon,
    CheckCircle as CheckCircleIcon,
    AccessTime as AccessTimeIcon,
    HourglassEmpty as HourglassTopIcon,
    Search as SearchIcon
} from '@material-ui/icons'
import Chart from 'react-apexcharts'
import TableComponent from 'components/shared/TableComponent'
import DsIcon from 'components/ds/Icon'
import { Tag } from 'components/ds/Badge'
import Loader from 'components/shared/Loader'
import NoDataIndication from 'components/shared/NoDataIndication'
import { useBrokerApplicationDashboard, useBrokersList } from './hooks/useBrokerApplicationDashboard'
import { useAppContext } from 'context/AppContext'
import strings from 'strings'
import './BrokerApplicationDashboard.scss'
import moment from 'moment'
import AuthService from 'tools/AuthService'

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(3)
    },
    pageHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(3),
        flexWrap: 'wrap',
        gap: theme.spacing(2)
    },
    pageTitle: {
        fontWeight: 700,
        fontSize: 'var(--t-page-title)',
        letterSpacing: 'var(--t-tracking-tight)',
        color: 'var(--fg-default)'
    },
    headerControls: {
        display: 'flex',
        gap: theme.spacing(2),
        alignItems: 'center',
        flexWrap: 'wrap'
    },
    formControl: {
        minWidth: 180,
        '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: '#fff'
        }
    },
    brokerFormControl: {
        minWidth: 250,
        '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: '#fff'
        }
    },
    brokerTag: {
        margin: '2px 4px 2px 0'
    },
    statCard: {
        backgroundColor: '#fff',
        borderRadius: 12,
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
        padding: theme.spacing(2.5),
        height: '100%',
        transition: 'box-shadow 0.2s ease',
        '&:hover': {
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.12)'
        }
    },
    statCardHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(2)
    },
    statIcon: {
        display: 'flex',
        alignItems: 'center',
        color: 'var(--fg-tertiary)',
        '& svg': { fontSize: 20 }
    },
    rowChevron: {
        color: 'var(--fg-tertiary)'
    },
    rangeNote: {
        fontSize: 'var(--t-caption)',
        color: 'var(--fg-tertiary)',
        marginTop: 4
    },
    iconBlue: {
        backgroundColor: 'rgba(63, 81, 181, 0.1)',
        color: '#3f51b5'
    },
    iconGreen: {
        backgroundColor: 'rgba(76, 175, 80, 0.1)',
        color: '#4caf50'
    },
    iconOrange: {
        backgroundColor: 'rgba(255, 152, 0, 0.1)',
        color: '#ff9800'
    },
    iconPurple: {
        backgroundColor: 'rgba(156, 39, 176, 0.1)',
        color: '#9c27b0'
    },
    statLabel: {
        fontSize: 14,
        fontWeight: 500,
        color: '#666'
    },
    statValue: {
        fontSize: 32,
        fontWeight: 700,
        color: '#1a1a1a',
        lineHeight: 1.2
    },
    chartCard: {
        backgroundColor: '#fff',
        borderRadius: 12,
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
        padding: theme.spacing(2.5),
        height: '100%'
    },
    chartTitle: {
        fontWeight: 600,
        fontSize: 16,
        color: '#1a1a1a',
        marginBottom: theme.spacing(2.5)
    },
    funnelContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        padding: theme.spacing(2.5, 0)
    },
    funnelItem: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        position: 'relative'
    },
    funnelBar: {
        width: '100%',
        height: 48,
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontWeight: 600,
        fontSize: 14,
        marginBottom: theme.spacing(1),
        transition: 'transform 0.2s ease',
        '&:hover': {
            transform: 'scale(1.02)'
        }
    },
    funnelSubmitted: {
        background: 'linear-gradient(135deg, #3f51b5, #5c6bc0)'
    },
    funnelInProgress: {
        background: 'linear-gradient(135deg, #ff9800, #ffb74d)'
    },
    funnelApproved: {
        background: 'linear-gradient(135deg, #4caf50, #81c784)'
    },
    funnelDenied: {
        background: 'linear-gradient(135deg, #f44336, #e57373)'
    },
    funnelLabel: {
        fontSize: 12,
        color: '#666',
        fontWeight: 500
    },
    funnelArrow: {
        position: 'absolute',
        right: -12,
        top: '35%',
        transform: 'translateY(-50%)',
        color: '#ccc',
        zIndex: 1
    },
    tableSection: {
        backgroundColor: '#fff',
        borderRadius: 12,
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
        padding: theme.spacing(2.5),
        marginTop: theme.spacing(3)
    },
    tableTitle: {
        fontWeight: 600,
        fontSize: 16,
        color: '#1a1a1a',
        marginBottom: theme.spacing(2)
    },
    statusChip: {
        borderRadius: 16,
        fontWeight: 500,
        fontSize: 12
    },
    statusDraft: {
        backgroundColor: 'rgba(63, 81, 181, 0.1)',
        color: '#3f51b5'
    },
    statusSubmitted: {
        backgroundColor: 'rgba(63, 81, 181, 0.1)',
        color: '#3f51b5'
    },
    statusInProgress: {
        backgroundColor: 'rgba(255, 152, 0, 0.1)',
        color: '#ff9800'
    },
    statusApproved: {
        backgroundColor: 'rgba(76, 175, 80, 0.1)',
        color: '#4caf50'
    },
    statusDeclined: {
        backgroundColor: 'rgba(244, 67, 54, 0.1)',
        color: '#f44336'
    },
    viewDetailsBtn: {
        textTransform: 'none',
        fontWeight: 500
    },
    loadingContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 400
    },
    searchField: {
        marginBottom: theme.spacing(1),
        width: '100%',
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#fff',
            '& input': {
                padding: '8px 12px',
                fontSize: '14px'
            }
        }
    }
}))

const MAX_BROKER_TAGS = 3

const DATE_RANGE_OPTIONS = [
    { value: 'last30', label: strings.BROKER_DASHBOARD.LAST_30_DAYS },
    { value: 'last60', label: strings.BROKER_DASHBOARD.LAST_60_DAYS },
    { value: 'last90', label: strings.BROKER_DASHBOARD.LAST_90_DAYS }
]

const StatCard = ({ icon, label, value, note }) => {
    const classes = useStyles()
    return (
        <Card className={classes.statCard} elevation={0}>
            <Box className={classes.statCardHeader}>
                <Box className={classes.statIcon}>
                    {icon}
                </Box>
                <Typography className={classes.statLabel}>{label}</Typography>
            </Box>
            <Typography className={classes.statValue}>{value}</Typography>
            {note && <Typography className={classes.rangeNote}>{note}</Typography>}
        </Card>
    )
}

const StatusChip = ({ status }) => {
    const classes = useStyles()
    const statusLower = status?.toLowerCase() || ''

    let chipClass = classes.statusDraft
    if (statusLower === 'submitted') chipClass = classes.statusSubmitted
    else if (statusLower === 'in progress' || statusLower === 'inprogress') chipClass = classes.statusInProgress
    else if (statusLower === 'approved') chipClass = classes.statusApproved
    else if (statusLower === 'declined' || statusLower === 'denied') chipClass = classes.statusDeclined

    return (
        <Chip
            label={status}
            size="small"
            className={`${classes.statusChip} ${chipClass}`}
        />
    )
}

const BrokerApplicationDashboard = () => {
    const history = useHistory()
    // All hooks must be called at the top level, before any conditional logic
    const classes = useStyles()
    const { isDemoMode } = useAppContext()
    const [dateRange, setDateRange] = useState('last30')
    const [selectedBrokerEmails, setSelectedBrokerEmails] = useState(['all_applications'])
    const [selectedBrokerOptions, setSelectedBrokerOptions] = useState([
        { id: 'all_applications', label: 'All Applications', value: 'all_applications' }
    ])
    // Applied selections that trigger API calls
    const [appliedBrokerOptions, setAppliedBrokerOptions] = useState([
        { id: 'all_applications', label: 'All Applications', value: 'all_applications' }
    ])

    const isBrokerCustomer = AuthService.isBrokerCustomer && AuthService.isBrokerCustomer()
    const currentUserEmail = AuthService.loggedInUser?.email || AuthService.getProfile()?.email

    const { brokers } = useBrokersList()

    const filters = useMemo(() => {
        if (isBrokerCustomer && currentUserEmail) {
            return {
                dateRange,
                brokerFilter: 'list',
                brokerEmails: [currentUserEmail]
            }
        }

        const appliedValues = appliedBrokerOptions.map(option => option.value)
        const isAllSelected = appliedValues.includes('all')
        const isAllApplicationsSelected = appliedValues.includes('all_applications')
        const isDirectSourceSelected = appliedValues.includes('direct_source')

        let brokerFilter = 'list'
        if (isAllSelected) {
            brokerFilter = 'all'
        } else if (isAllApplicationsSelected) {
            brokerFilter = 'all_applications'
        } else if (isDirectSourceSelected) {
            brokerFilter = 'direct_source'
        }

        return {
            dateRange,
            brokerFilter,
            brokerEmails: (isAllSelected || isAllApplicationsSelected || isDirectSourceSelected) ? '' : appliedValues.join(',')
        }
    }, [dateRange, appliedBrokerOptions, isBrokerCustomer, currentUserEmail])

    const { data, loading, error } = useBrokerApplicationDashboard(filters, isDemoMode)

    // Filter valid brokers for the Autocomplete options
    const validBrokers = useMemo(() => {
        if (!Array.isArray(brokers)) {
            return []
        }

        return brokers.filter(broker => {
            if (!broker) return false
            const email = broker.email || broker.brokerEmail
            return email && email.trim() !== ''
        })
    }, [brokers])

    const autocompleteOptions = useMemo(() => [
        { id: 'all_applications', label: 'All Applications', value: 'all_applications' },
        { id: 'all', label: strings.BROKER_DASHBOARD.ALL_BROKERS, value: 'all' },
        { id: 'direct_source', label: 'Direct Applications', value: 'direct_source' },
        ...validBrokers.map(broker => ({
            id: broker.email || broker.brokerEmail,
            label: `${broker.name || broker.brokerName || 'Unknown Broker'} (${broker.email || broker.brokerEmail})`,
            value: broker.email || broker.brokerEmail
        }))
    ], [validBrokers])

    const handleDateRangeChange = (event) => {
        setDateRange(event.target.value)
    }

    const activityTimelineChart = useMemo(() => {
        if (!data?.activityTimeline) return { options: {}, series: [] }

        const dates = Object.keys(data.activityTimeline).sort()
        const chartData = dates.map(date => ({
            x: date,
            y: data.activityTimeline[date]
        }))

        return {
            options: {
                chart: {
                    type: 'area',
                    toolbar: { show: false },
                    zoom: { enabled: false }
                },
                colors: ['#6f5bd8'],
                dataLabels: { enabled: false },
                stroke: {
                    curve: 'smooth',
                    width: 2
                },
                fill: {
                    type: 'gradient',
                    gradient: {
                        shadeIntensity: 1,
                        opacityFrom: 0.4,
                        opacityTo: 0.1,
                        stops: [0, 90, 100]
                    }
                },
                xaxis: {
                    type: 'datetime',
                    labels: {
                        format: 'MM/dd',
                        rotate: -45,
                        rotateAlways: false,
                        hideOverlappingLabels: true,
                        style: { fontSize: '10px' },
                        datetimeUTC: false
                    },
                    tickAmount: 10,
                    axisBorder: {
                        show: true
                    },
                    axisTicks: {
                        show: true
                    }
                },
                yaxis: {
                    title: { text: strings.BROKER_DASHBOARD.SUBMISSIONS },
                    min: 0,
                    forceNiceScale: true
                },
                tooltip: {
                    enabled: true,
                    x: {
                        format: 'MM/dd/yyyy'
                    },
                    shared: false,
                    intersect: true
                },
                grid: {
                    borderColor: '#e0e0e0',
                    strokeDashArray: 4
                },
                markers: {
                    size: 4,
                    hover: {
                        size: 6
                    }
                }
            },
            series: [{
                name: strings.BROKER_DASHBOARD.SUBMISSIONS,
                data: chartData
            }]
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(data?.activityTimeline)])

    const funnelBarChart = useMemo(() => {
        const statusData = data?.applicationsStatus || {}

        return {
            options: {
                chart: {
                    type: 'bar',
                    toolbar: { show: false }
                },
                plotOptions: {
                    bar: {
                        horizontal: false,
                        borderRadius: 6,
                        distributed: true,
                        columnWidth: '60%',
                        dataLabels: {
                            position: 'top'
                        }
                    }
                },
                colors: ['#6f5bd8', '#ea6f39', '#2f7d63', '#a93b22'],
                dataLabels: {
                    enabled: false
                },
                xaxis: {
                    categories: [
                        strings.BROKER_DASHBOARD.SUBMITTED,
                        strings.BROKER_DASHBOARD.IN_PROGRESS_STATUS,
                        strings.BROKER_DASHBOARD.APPROVED,
                        strings.BROKER_DASHBOARD.DENIED
                    ],
                    labels: {
                        style: {
                            fontSize: '12px',
                            fontWeight: 500
                        }
                    }
                },
                yaxis: {
                    labels: {
                        style: { fontSize: '12px' }
                    }
                },
                tooltip: {
                    enabled: true,
                    y: {
                        formatter: (val) => `${val} ${strings.BROKER_DASHBOARD.APPLICATIONS || 'Applications'}`
                    }
                },
                legend: { show: false },
                grid: {
                    borderColor: '#e0e0e0',
                    strokeDashArray: 4,
                    xaxis: { lines: { show: false } },
                    yaxis: { lines: { show: true } }
                }
            },
            series: [{
                name: strings.APPLICATIONS,
                data: [
                    statusData.totalCount || 0,
                    statusData.inprogressCount || 0,
                    statusData.approvedCount || 0,
                    statusData.declinedCount || 0
                ]
            }]
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(data?.applicationsStatus)])

    const tableColumns = [
        {
            dataField: 'applicationName',
            text: strings.BROKER_DASHBOARD.APPLICATION_NAME,
        },
        {
            dataField: 'applicationStatus',
            text: strings.BROKER_DASHBOARD.STATUS,
            formatter: (cell) => <StatusChip status={cell?.status} />
        },
        {
            dataField: 'applicationCreatedAt',
            text: strings.BROKER_DASHBOARD.SUBMITTED_DATE_TIME,
            formatter: (cell) => {
                return moment(cell).format('MM/DD/YYYY hh:mm A')
            }
        },
        {
            dataField: 'completionTimeInDaysHours',
            text: strings.BROKER_DASHBOARD.TIME_IN_CURRENT_STAGE,
        },
        {
            dataField: 'action',
            text: '',
            sort: false,
            headerStyle: { width: '3rem' },
            formatter: () => (
                <DsIcon name='chevron-right' size={16} className={classes.rowChevron} />
            )
        }
    ]

    if (loading) {
        return (
            <Box className={classes.root}>
                <Box className={classes.loadingContainer}>
                    <Loader />
                </Box>
            </Box>
        )
    }

    if (error) {
        return (
            <Box className={classes.root}>
                <NoDataIndication message={strings.BROKER_DASHBOARD.FAILED_TO_LOAD} />
            </Box>
        )
    }

    const {
        totalApplications = 0,
        approvalRate = 0,
        averageDays = 0,
        inprogressCount = 0,
        brokersApplication = []
    } = data || {}

    const rangeNote = (DATE_RANGE_OPTIONS.find(o => o.value === dateRange) || {}).label

    return (
        <Box className={classes.root}>
            <Box className={classes.pageHeader}>
                <Typography className={classes.pageTitle}>
                    {strings.BROKER_DASHBOARD.PAGE_TITLE}
                </Typography>
                <Box className={classes.headerControls}>
                    <FormControl variant="outlined" size="small" className={classes.formControl}>
                        <InputLabel id="date-range-label">{strings.BROKER_DASHBOARD.DATE_RANGE_LABEL}</InputLabel>
                        <Select
                            labelId="date-range-label"
                            value={dateRange}
                            onChange={handleDateRangeChange}
                            label={strings.BROKER_DASHBOARD.DATE_RANGE_LABEL}
                        >
                            {DATE_RANGE_OPTIONS.map(option => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {!isBrokerCustomer && (
                        <Autocomplete
                        multiple
                        id="broker-select"
                        size="small"
                        disableCloseOnSelect
                        options={autocompleteOptions}
                        value={selectedBrokerOptions}
                        onChange={(event, newValue) => {
                            console.log('Autocomplete onChange (pending):', newValue)

                            if (newValue.length === 0) {
                                // If nothing selected (clear icon clicked), default to 'all_applications'
                                const allApplicationsOption = { id: 'all_applications', label: 'All Applications', value: 'all_applications' }
                                setSelectedBrokerOptions([allApplicationsOption])
                                setAppliedBrokerOptions([allApplicationsOption])
                                setSelectedBrokerEmails(['all_applications'])
                                return
                            }

                            const specialOptions = ['all', 'all_applications', 'direct_source']
                            const lastSelected = newValue[newValue.length - 1]

                            if (specialOptions.includes(lastSelected.value)) {
                                setSelectedBrokerOptions([lastSelected])
                            } else {
                                const filteredValue = newValue.filter(option => !specialOptions.includes(option.value))
                                setSelectedBrokerOptions(filteredValue)
                            }
                        }}
                        onClose={() => {
                            console.log('Dropdown closed, applying selections:', selectedBrokerOptions)
                            setAppliedBrokerOptions(selectedBrokerOptions)
                            setSelectedBrokerEmails(selectedBrokerOptions.map(option => option.value))
                        }}
                        onOpen={() => {
                            console.log('Dropdown opened')
                        }}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        renderOption={(option, { selected }) => (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Checkbox
                                    style={{ marginRight: 8 }}
                                    checked={selected}
                                />
                                <span>{option.label}</span>
                            </div>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                label={strings.BROKER_DASHBOARD.SELECT_BROKERS_LABEL}
                                placeholder={appliedBrokerOptions.length ? '' : strings.BROKER_DASHBOARD.SEARCH_BROKERS_PLACEHOLDER}
                                className={classes.brokerFormControl}
                            />
                        )}
                        renderTags={() => {
                            const displayValue = appliedBrokerOptions
                            const resetToAll = () => {
                                const allApplicationsOption = { id: 'all_applications', label: 'All Applications', value: 'all_applications' }
                                setSelectedBrokerOptions([allApplicationsOption])
                                setAppliedBrokerOptions([allApplicationsOption])
                                setSelectedBrokerEmails(['all_applications'])
                            }

                            const special = displayValue.find(tag => ['all', 'all_applications', 'direct_source'].includes(tag.value))
                            if (special) {
                                return [
                                    <Tag key='special-tag' className={classes.brokerTag} onRemove={(e) => { e.stopPropagation(); resetToAll() }} removeLabel={`Remove ${special.label}`}>
                                        {special.label}
                                    </Tag>
                                ]
                            }

                            const removeBroker = (value) => {
                                const next = displayValue.filter(tag => tag.value !== value)
                                if (next.length === 0) {
                                    resetToAll()
                                    return
                                }
                                setSelectedBrokerOptions(next)
                                setAppliedBrokerOptions(next)
                                setSelectedBrokerEmails(next.map(option => option.value))
                            }

                            const shown = displayValue.slice(0, MAX_BROKER_TAGS)
                            const hidden = displayValue.length - shown.length

                            return [
                                ...shown.map(tag => (
                                    <Tag
                                        key={tag.value}
                                        className={classes.brokerTag}
                                        onRemove={(e) => { e.stopPropagation(); removeBroker(tag.value) }}
                                        removeLabel={`Remove ${tag.label}`}
                                    >
                                        {tag.label}
                                    </Tag>
                                )),
                                ...(hidden > 0
                                    ? [<Tag key='more-tag' className={classes.brokerTag}>{`+${hidden}`}</Tag>]
                                    : [])
                            ]
                        }}
                        style={{ minWidth: 250 }}
                    />
                    )}
                </Box>
            </Box>

            <Grid container spacing={3} style={{ marginBottom: 24 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        icon={<AssignmentIcon />}
                        label={strings.BROKER_DASHBOARD.TOTAL_SUBMISSIONS}
                        value={totalApplications}
                        note={rangeNote}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        icon={<CheckCircleIcon />}
                        label={strings.BROKER_DASHBOARD.APPROVAL_RATE}
                        value={`${Number(approvalRate).toFixed(2)}%`}
                        note={rangeNote}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        icon={<AccessTimeIcon />}
                        label={strings.BROKER_DASHBOARD.AVG_PIPELINE_VELOCITY}
                        value={`${averageDays} ${strings.BROKER_DASHBOARD.DAYS}`}
                        note={rangeNote}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        icon={<HourglassTopIcon />}
                        label={strings.BROKER_DASHBOARD.IN_PROGRESS}
                        value={inprogressCount}
                        note={rangeNote}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Card className={classes.chartCard} elevation={0}>
                        <Typography className={classes.chartTitle}>
                            {strings.BROKER_DASHBOARD.APPLICATION_STATUS_FUNNEL}
                        </Typography>
                        <Chart
                            options={funnelBarChart.options}
                            series={funnelBarChart.series}
                            type="bar"
                            height={280}
                        />
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card className={classes.chartCard} elevation={0}>
                        <Typography className={classes.chartTitle}>
                            {strings.BROKER_DASHBOARD.SUBMISSIONS_PER_DAY}
                        </Typography>
                        {data?.activityTimeline && Object.keys(data.activityTimeline).length > 0 ? (
                            <Chart
                                options={activityTimelineChart.options}
                                series={activityTimelineChart.series}
                                type="area"
                                height={280}
                            />
                        ) : (
                            <NoDataIndication message={strings.BROKER_DASHBOARD.NO_ACTIVITY_DATA} />
                        )}
                    </Card>
                </Grid>
            </Grid>

            <Box className={classes.tableSection}>
                <Typography className={classes.tableTitle}>
                    {strings.BROKER_DASHBOARD.RECENT_APPLICATION_ACTIVITY}
                </Typography>
                {brokersApplication && brokersApplication.length > 0 ? (
                    <TableComponent
                        data={brokersApplication}
                        columns={tableColumns}
                        keyField="id"
                        showPagination={false}
                        onRowClick={(row) => history.push(`/applicant/${row.applicationUuid}`)}
                    />
                ) : (
                    <NoDataIndication message={strings.BROKER_DASHBOARD.NO_RECENT_APPLICATIONS} />
                )}
            </Box>
        </Box>
    )
}

export default BrokerApplicationDashboard
